import type { RateOutput } from '@allocab/allocab-extranet-api-types';
import { useQuery } from '@tanstack/react-query';

import type { DashboardRateInput, DashboardRateResponse, RangeInput } from '../../../types/extranet-api.types';
import { getExtranetApi } from '../extranetApi';
import { toRateInput } from '../mappers';
import { extranetQueryKeys } from '../queryKeys';

type RateMetric =
  | 'coverage-rate'
  | 'punctuality-rate'
  | 'incident-rate'
  | 'security-incident-rate'
  | 'service-rate'
  | 'late-customer-cancellation-rate'
  | 'no-show-passenger-rate';

const fetchRate = async (metric: RateMetric, range: RangeInput): Promise<number> => {
  // Back livre `rate` en ratio 0..1 (ex. 0.92 = 92%) — on multiplie par 100 pour
  // matcher la convention front (KpiCard / formatPercent attendent un %).
  const data = await getExtranetApi().get<RangeInput, RateOutput>(`/v1/performance/${metric}`, range);
  return data.rate * 100;
};

/**
 * Période précédente de **même durée**, collée à celle affichée : c'est ce que
 * promet le libellé « vs période précédente » de `KpiCard`.
 */
const previousRange = (range: RangeInput): RangeInput => {
  const span = range.endDate - range.startDate;
  return { ...range, startDate: range.startDate - span, endDate: range.startDate };
};

/**
 * Un taux et son évolution. L'API ne renvoie que `{ rate }` pour une période
 * donnée : le trend est donc calculé ici, en interrogeant une seconde fois la
 * période précédente. L'écart est en **points de pourcentage**, comme l'attend
 * `formatTrend`.
 *
 * Limite connue : un taux de 0 % ne se distingue pas d'une absence de données
 * côté back, une période précédente vide se lit donc comme un vrai 0 %.
 */
const fetchRateWithTrend = async (metric: RateMetric, input: DashboardRateInput): Promise<DashboardRateResponse> => {
  const range = toRateInput(input);
  const [value, previous] = await Promise.all([fetchRate(metric, range), fetchRate(metric, previousRange(range))]);
  return { value, trend: value - previous };
};

const useRate = (metric: RateMetric, input: DashboardRateInput) =>
  useQuery({
    queryKey: extranetQueryKeys.dashboard.rate(metric, input),
    queryFn: () => fetchRateWithTrend(metric, input),
    enabled: Boolean(input.dateRange?.from && input.dateRange?.to),
  });

export const useCoverageRate = (input: DashboardRateInput) => useRate('coverage-rate', input);
export const usePunctualityRate = (input: DashboardRateInput) => useRate('punctuality-rate', input);
export const useIncidentRate = (input: DashboardRateInput) => useRate('incident-rate', input);
export const useSecurityIncidentRate = (input: DashboardRateInput) => useRate('security-incident-rate', input);
export const useServiceRate = (input: DashboardRateInput) => useRate('service-rate', input);
export const useLateCancellationRate = (input: DashboardRateInput) => useRate('late-customer-cancellation-rate', input);
export const useNoShowPassengerRate = (input: DashboardRateInput) => useRate('no-show-passenger-rate', input);
